import {
  storefrontAbTestIdFormat,
  storefrontTreatmentIdFormat,
} from "@redotech/redo-coverage-id";
import * as z from "zod";
import { CoverageProductControlWriteSchema } from "./coverage-product-control";
import { StorefrontTreatmentSettingsSchema } from "./storefront-treatment-settings";

/** A storefront coverage A/B test as stored in CockroachDB (storefront_ab_test). */
export const StorefrontAbTestSchema = z.object({
  id: storefrontAbTestIdFormat.schema,
  name: z.string(),
  active: z.boolean(),
  startAt: z.date().nullable(),
  endAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type StorefrontAbTest = z.infer<typeof StorefrontAbTestSchema>;

/** The fields a merchant may change on a test. */
export const StorefrontAbTestWriteSchema = StorefrontAbTestSchema.pick({
  name: true,
  active: true,
  startAt: true,
  endAt: true,
}).extend({ name: z.string().trim().min(1).max(200) });
export type StorefrontAbTestWrite = z.infer<typeof StorefrontAbTestWriteSchema>;

export const StorefrontAbTestPatchSchema =
  StorefrontAbTestWriteSchema.partial();
export type StorefrontAbTestPatch = z.infer<typeof StorefrontAbTestPatchSchema>;

/** One arm of a test (storefront_treatment); weight is its share of traffic. */
export const StorefrontTreatmentSchema = z.object({
  id: storefrontTreatmentIdFormat.schema,
  name: z.string(),
  weight: z.number().nonnegative(),
  /** False for a no-coverage arm. */
  coverageEnabled: z.boolean(),
  sortIndex: z.number().int(),
});
export type StorefrontTreatment = z.infer<typeof StorefrontTreatmentSchema>;

/** An arm's current pricing: one control per coverage product it has saved. */
export const StorefrontTreatmentPricingSchema = z.array(
  CoverageProductControlWriteSchema,
);
export type StorefrontTreatmentPricing = z.infer<
  typeof StorefrontTreatmentPricingSchema
>;

/** A test with its arms and each arm's coverage settings and pricing. */
export const StorefrontAbTestAndTreatmentsSchema = z.object({
  test: StorefrontAbTestSchema,
  treatments: z.array(
    StorefrontTreatmentSchema.extend({
      settings: StorefrontTreatmentSettingsSchema,
      pricing: StorefrontTreatmentPricingSchema,
    }),
  ),
});
export type StorefrontAbTestAndTreatments = z.infer<
  typeof StorefrontAbTestAndTreatmentsSchema
>;

/**
 * One arm of the account's running test as autocheck sends it to the
 * storefront, which draws an arm by weight and stores its id in the
 * `redo_treatment_id` cart attribute.
 */
export const StorefrontAutocheckTreatmentSchema =
  StorefrontTreatmentSchema.pick({
    id: true,
    name: true,
    weight: true,
    coverageEnabled: true,
  }).extend({
    settings: StorefrontTreatmentSettingsSchema,
    /** In the shopper's currency; absent from older servers, so the team's pricing applies. */
    pricing: StorefrontTreatmentPricingSchema.optional(),
  });
export type StorefrontAutocheckTreatment = z.infer<
  typeof StorefrontAutocheckTreatmentSchema
>;
